import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, ChevronRight } from 'lucide-react';
import { LogoIcon } from '@/components/common/Logo';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ThemeToggle } from './ThemeToggle';
import { SidebarUsageMeter } from './SidebarUsageMeter';
import { navItems } from './navConfig';
import { useUnreadSummary } from '@/hooks/useUnreadSummary';
import { useSidebarStore } from '@/stores/useSidebarStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { useBillingStore } from '@/stores/useBillingStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { cn, getInitials } from '@/lib/utils';
import { toast } from 'sonner';

export const MobileNav: React.FC = () => {
  const { isMobileOpen, setMobileOpen } = useSidebarStore();
  const { user, logout } = useAuthStore();
  const { currentRole } = useWorkspaceStore();
  const location = useLocation();
  const { formattedBadge } = useUnreadSummary();
  const navigate = useNavigate();
  const billingStatus = useBillingStore((state) => state.status);
  const hasCustomerAccess = billingStatus ? Boolean(billingStatus.allowedFeatures?.includes('CustomersPageAccess')) : true;

  const handleLogout = () => {
    setMobileOpen(false);
    logout();
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    toast.info('Sessão encerrada');
    navigate('/login', { replace: true });
  };

  const userName = user?.fullName || 'Diego Fernandes';
  const userEmail = user?.email || 'admin@taleshq.com';
  const initials = getInitials(userName, userEmail);

  return (
    <Sheet open={isMobileOpen} onOpenChange={setMobileOpen}>
      <SheetContent side="left" className="w-72 p-0 flex flex-col bg-card">
        {/* Header */}
        <SheetHeader className="p-4 border-b border-border text-left">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 flex items-center justify-center shrink-0">
              <LogoIcon className="h-7 w-7 text-primary" />
            </div>
            <div>
              <SheetTitle className="text-base font-bold tracking-tight">
                Tales
              </SheetTitle>
              <p className="text-xs text-muted-foreground">
                Navegação Móvel
              </p>
            </div>
          </div>
        </SheetHeader>

        {/* Navigation list */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems
            .filter((item) => !item.allowedRoles || (currentRole && item.allowedRoles.includes(currentRole)))
            .map((baseItem) => {
              const item = { ...baseItem };
              if (item.to === '/conversations') {
                item.badge = formattedBadge;
                if (formattedBadge) item.badgeVariant = 'default';
              }
              if (item.to === '/customers' && !hasCustomerAccess) {
                item.badge = 'Pro';
                item.badgeVariant = 'secondary';
              }
              const Icon = item.icon;
              const isActive =
                item.to === '/conversations'
                  ? location.pathname.startsWith('/conversations')
                  : location.pathname === item.to;

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    'flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-accent text-accent-foreground font-semibold'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Icon className={cn('h-5 w-5', isActive ? 'text-primary' : 'text-muted-foreground')} />
                    <span>{item.label}</span>
                  </div>
                  {item.badge && (
                    <Badge variant={item.badgeVariant || 'default'} className="text-[10px] px-1.5 py-0 h-4">
                      {item.badge}
                    </Badge>
                  )}
                </NavLink>
              );
            })}
        </nav>

        {/* Bottom actions */}
        <div className="p-4 border-t border-border space-y-3 bg-muted/20">
          {currentRole === 'Owner' && <SidebarUsageMeter isExpanded={true} onNavigate={() => setMobileOpen(false)} />}

          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Aparência</span>
            <ThemeToggle />
          </div>

          <div className="pt-2 border-t border-border/60 space-y-1.5">
            <button
              onClick={() => {
                setMobileOpen(false);
                navigate('/profile');
              }}
              className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-muted transition-colors text-left"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Avatar className="h-8 w-8 border border-border">
                  <AvatarFallback className="bg-primary/10 text-primary font-bold text-xs">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col min-w-0">
                  <span className="text-xs font-medium text-foreground truncate">{userName}</span>
                  <span className="text-[10px] text-muted-foreground truncate">{userEmail}</span>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>

            <div className="flex items-center justify-end px-2 pt-1">
              <button
                onClick={handleLogout}
                className="text-xs text-destructive hover:text-destructive/80 font-medium flex items-center gap-1.5 transition-colors"
              >
                <LogOut className="h-3.5 w-3.5" />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

